import { MemberInterface } from "../Members/Members.Interface";
import { SortDirection } from "../Response/SortDirection";
import { SessionInterface } from "../Sessions/Sessions.Interface";
import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { TwilioCallStatus } from "../Voice/Twilio.Interface";

//LINK - libs/entities/src/Call/Call.Entity.ts
export interface CallInterface extends OrgBaseInterface {
  callSid: string;
  subAccountSid?: string;
  conferenceSid?: string;
  sessionId?: number;
  session?: SessionInterface;
  callStatus?: TwilioCallStatus;
  callDirection: CallDirection;
  callDuration?: number; // Duration in seconds
  userPhoneNumber?: string;
  botPhoneNumber?: string;
  memberId?: number; // Agent/Member who handled the call
  member?: MemberInterface;
  amdStatus?: AmdStatus; // Answering Machine Detection status
  recordingUrl?: string;
  locationId?: number;
  webhookHistory?: any[]; // All webhook history for the call
}

/**
 * Sort columns for call recordings
 */
export enum CallRecordingSortColumn {
  CREATED_AT = "createdAt",
  CALL_DURATION = "callDuration",
  CALL_DIRECTION = "callDirection",
  CALL_OUTCOME = "callOutcome",
  USER_NAME = "userName",
  USER_PHONE_NUMBER = "userPhoneNumber",
  LOCATION_NAME = "locationName",
  AGENT_NAME = "agentName"
}

/**
 * Sort criteria for call recordings
 */
export interface CallRecordingSortCriteria {
  column: CallRecordingSortColumn;
  direction: SortDirection;
}

/**
 * Query parameters for call recordings endpoint (cursor-based)
 */
export interface CallRecordingsQueryParams {
  locationId?: number;
  after?: string;
  before?: string;
  limit?: number;
  sort?: string[];
}

/**
 * Query parameters for call recordings list endpoint (offset-based)
 */
export interface CallRecordingsListQueryParams {
  locationId?: number;
  offset?: number;
  limit?: number;
  sort?: string[];
  /**
   * Full-text search across userName, userPhoneNumber, locationName, and agentName.
   * Uses case-insensitive partial matching (ILIKE '%search%').
   *
   * Performance note: For large datasets, consider adding GIN trigram indexes:
   * - CREATE INDEX idx_call_user_phone_trgm ON call USING gin (user_phone_number gin_trgm_ops);
   * - CREATE INDEX idx_user_name_trgm ON "user" USING gin (name gin_trgm_ops);
   * - CREATE INDEX idx_location_name_trgm ON location USING gin (name gin_trgm_ops);
   * - CREATE INDEX idx_member_agent_name_trgm ON member USING gin (agent_name gin_trgm_ops);
   */
  search?: string;
  userId?: number;
}

/**
 * Derived call outcome from TwilioCallStatus
 */
export enum CallOutcome {
  ANSWERED = "Answered",
  NO_ANSWER = "No Answer",
  VOICEMAIL = "Voicemail",
  BUSY = "Busy",
  FAILED = "Failed",
  CANCELED = "Canceled",
  IN_PROGRESS = "In Progress",
  UNKNOWN = "Unknown"
}

/**
 * A single event in the call history timeline
 */
export interface CallHistoryItem {
  description: string; // Pretty name of the interaction (e.g., "swivl called customer")
  timeSeconds: number; // Time in seconds from call start
}

/**
 * Response type for call recordings list UI
 */
export interface CallRecordingResponse {
  callSid: string;
  userId: number;
  userName: string; // From session -> user -> name
  callDuration?: number; // Duration in seconds
  callDirection: CallDirection;
  callOutcome: CallOutcome; // Derived from callStatus and amdStatus
  agentName: string; // From deriveAgentString(call)
  createdAt: Date; // createdAt from callEntity
  userPhoneNumber?: string; // userPhoneNumber from callEntity
  locationName?: string; // location -> name
  recordingUrl?: string;
  history?: CallHistoryItem[]; // Event history timeline
}

export enum CallDirection {
  INBOUND = "inbound",
  OUTBOUND = "outbound"
}

export enum AmdStatus {
  MACHINE_START = "machine_start",
  MACHINE_END_BEEP = "machine_end_beep",
  MACHINE_END_SILENCE = "machine_end_silence",
  MACHINE_END_OTHER = "machine_end_other",
  HUMAN = "human",
  FAX = "fax",
  UNKNOWN = "unknown"
}
